// src/pages/Home.jsx

import React, { useEffect, useState } from "react";
import { useUser } from "../context/UserContext";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../context/TenantContext";
import { useNavigate } from "react-router-dom";
import { getStaff } from "../services/staffService";
import { clientService } from "../services/clientService";
import appointmentService from "../services/appointmentService";
import { getRecentActivities, getActivityStats } from "../services/activityService";
import { tenantDataService } from "../services/tenantDataService";
import feedbackService from "../services/feedbackService";
import { invoiceService } from "../services/invoiceService";
import BookingRequestsTile from "../components/admin/BookingRequestsTile";

import {
  FiUsers,
  FiCalendar,
  FiClock,
  FiTrendingUp,
  FiActivity,
  FiAlertCircle,
  FiCheckCircle,
  FiPlus,
  FiArrowRight,
  FiGrid,
  FiFileText,
  FiStar,
  FiUserCheck,
  FiDollarSign,
  FiUser,
  FiAlertTriangle
} from 'react-icons/fi';

export default function Home() {
  const { user } = useUser();
  const { userRole, isFirebaseAvailable, currentUser } = useAuth();
  const { currentTenant, loading: tenantLoading } = useTenant();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);

  // Load all data when tenant context is ready
  useEffect(() => {
    console.log('🔍 Dashboard useEffect: currentTenant?.id =', currentTenant?.id, 'user?.userId =', user?.userId, 'userRole =', userRole);
    if (currentTenant?.id && userRole) {
      console.log('✅ Dashboard: Conditions met, loading dashboard data...');
      loadDashboardData();
    } else {
      console.log('❌ Dashboard: Conditions not met - currentTenant?.id:', currentTenant?.id, 'userRole:', userRole);
    }
  }, [currentTenant?.id, user?.userId, userRole]);

  // Listen for leave status updates to refresh pending requests
  useEffect(() => {
    const handleLeaveStatusUpdate = (event) => {
      const { newStatus } = event.detail;

      // If a leave request was approved or declined, remove it from pending list
      if (newStatus === 'approved' || newStatus === 'declined') {
        setPendingLeaveRequests(prevRequests =>
          prevRequests.filter(req => req.id !== event.detail.requestId)
        );
      }
    };

    window.addEventListener('leave-status-updated', handleLeaveStatusUpdate);

    return () => {
      window.removeEventListener('leave-status-updated', handleLeaveStatusUpdate);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load core data first (appointments, clients, and invoices are required)
      const [appointmentsData, clientsData, invoicesData] = await Promise.all([
        appointmentService.getAllAppointments(),
        clientService.getAllClients(),
        invoiceService.getAllInvoices()
      ]);

      setAppointments(appointmentsData);
      setClients(clientsData);
      setInvoices(invoicesData);


      // Load optional data (activities and feedback) separately to avoid blocking dashboard
      try {
        const [activityData, statsData, feedbackData] = await Promise.all([
          getRecentActivities(6), // Get 6 most recent activities
          getActivityStats('today'),
          feedbackService.getDashboardSummary(currentTenant.id)
        ]);
        setRecentActivity(activityData);
        setActivityStats(statsData);
        setFeedbackSummary(feedbackData);
      } catch (activityError) {
        console.warn('Activity data unavailable:', activityError.message);
        setRecentActivity([]);
        setActivityStats(null);
        setFeedbackSummary(null);
      }


      // Load staff data
      try {
        const staff = await getStaff();
        setStaffList(staff);
      } catch (error) {
        console.error('Error loading staff:', error);
        setStaffList([]);
      }

      // Load pending leave requests for admin users
      console.log('🔍 Dashboard: userRole =', userRole, 'currentTenant.id =', currentTenant?.id);
      if (userRole === 'admin') {
        try {
          console.log('🏥 Dashboard: Setting tenant context to:', currentTenant.id);
          tenantDataService.setTenantContext(currentTenant.id);

          console.log('📋 Dashboard: Fetching all leave requests...');
          const leaveRequests = await tenantDataService.getAll('leaveRequests');
          console.log('📋 Dashboard: Raw leave requests response:', leaveRequests);
          console.log('📋 Dashboard: Leave requests count:', leaveRequests?.length || 0);

          if (leaveRequests && leaveRequests.length > 0) {
            console.log('📋 Dashboard: Leave request details:');
            leaveRequests.forEach((req, index) => {
              console.log(`  ${index + 1}. ID: ${req.id}, Status: ${req.status}, Staff: ${req.staffName || req.staffId}, Type: ${req.leaveType}`);
            });

            const pending = leaveRequests.filter(req => req.status === 'pending');
            console.log('⏳ Dashboard: Filtered pending requests:', pending);
            console.log('⏳ Dashboard: Pending count:', pending.length);
            setPendingLeaveRequests(pending);
          } else {
            console.log('📋 Dashboard: No leave requests found');
            setPendingLeaveRequests([]);
          }
        } catch (error) {
          console.error('❌ Dashboard: Error loading leave requests:', error);
          console.error('❌ Dashboard: Error details:', {
            message: error.message,
            code: error.code,
            stack: error.stack
          });
          setPendingLeaveRequests([]);
        }
      } else {
        console.log('❌ Dashboard: Not loading leave requests - user is not admin (role:', userRole, ')');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Fallback to empty arrays if there's an error
      setAppointments([]);
      setClients([]);
      setStaffList([]);
    }
  };

  // Calculate statistics with role-based filtering
  const today = new Date().toISOString().split('T')[0];
  const currentUserId = currentUser?.uid || user?.userId;

  // Role-based calculations
  const effectiveRole = isFirebaseAvailable ? userRole : user.role;
  const role = effectiveRole?.toLowerCase() || 'staff';

  // Filter appointments based on user role
  const getFilteredAppointments = (appointments, filterType = 'all') => {
    console.log('🔍 getFilteredAppointments:', {
      role,
      filterType,
      currentUserId,
      appointmentsCount: appointments.length,
      sampleAppointment: appointments[0]
    });

    if (role === 'admin') {
      // Admin sees their own appointments for personal stats
      if (filterType === 'personal') {
        const filtered = appointments.filter(appt => appt.staffId === currentUserId);
        console.log('🎯 Admin personal filter result:', filtered.length, 'appointments');
        return filtered;
      }
      // Admin sees all clinic appointments for clinic-wide stats
      return appointments;
    } else if (role === 'clinician') {
      // Clinicians only see their own appointments
      const filtered = appointments.filter(appt => appt.staffId === currentUserId);
      console.log('🎯 Clinician filter result:', filtered.length, 'appointments');
      return filtered;
    }
    // Staff sees all clinic appointments
    return appointments;
  };

  // For personal stats (top 4 tiles), always use personal filtering for ALL roles
  const allTodayAppointments = appointments.filter(appt => appt.date === today);
  console.log('🔍 Dashboard Debug:', {
    today,
    currentUserId,
    totalAppointments: appointments.length,
    todayAppointments: allTodayAppointments.length,
    sampleAppointment: appointments[0],
    sampleTodayAppointment: allTodayAppointments[0],
    userInfo: { userId: user?.userId, id: user?.id, role }
  });

  const personalTodayAppointments = allTodayAppointments.filter(appt => appt.staffId === currentUserId);
  console.log('🎯 Personal filtering result:', {
    personalAppointments: personalTodayAppointments.length,
    samplePersonal: personalTodayAppointments[0]
  });

  const todayAppointments = personalTodayAppointments; // Top 4 tiles show personal stats for everyone

  // Filter clients based on personal appointments - works for any user role
  const getFilteredClients = () => {
    // For personal stats, ALL users see clients from their personal appointments
    const personalClientIds = personalTodayAppointments.map(appt => appt.clientId);
    return clients.filter(client => personalClientIds.includes(client.id));
  };

  const filteredClients = getFilteredClients();

  // Fix pending notes logic: include all unfinalized notes for attended appointments
  const getPendingNotes = () => {
    // Get ALL Holly's appointments (not just today), filter for red/amber circles (pending notes)
    const personalAppointments = appointments.filter(appt => appt.staffId === currentUserId);
    const personalAttended = personalAppointments.filter(appt => appt.status === 'attended');
    const personalPendingNotes = personalAttended.filter(appt =>
      appt.noteStatus === 'red' ||
      appt.noteStatus === 'amber' ||
      appt.noteStatus === undefined ||
      appt.noteStatus === null
    );

    console.log('🔍 Pending Notes Debug:', {
      totalAppointments: appointments.length,
      personalAppointments: personalAppointments.length,
      personalAttended: personalAttended.length,
      personalPendingNotes: personalPendingNotes.length,
      currentUserId
    });

    console.log('📋 All Attended Appointments:', personalAttended.map(appt => ({
      id: appt.id,
      date: appt.date,
      status: appt.status,
      noteStatus: appt.noteStatus,
      clientName: appt.clientName
    })));

    console.log('🔴 Filtered Pending Notes:', personalPendingNotes.map(appt => ({
      id: appt.id,
      date: appt.date,
      noteStatus: appt.noteStatus,
      clientName: appt.clientName
    })));

    return personalPendingNotes;
  };

  const pendingNotes = getPendingNotes();
  const completedToday = todayAppointments.filter(appt => appt.noteStatus === 'green').length;

  // Find next appointment (role-specific)
  const currentTime = new Date();
  const currentHour = currentTime.getHours();
  const currentMinute = currentTime.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  // Use personal appointments for all users to find next appointment
  const appointmentsForNext = personalTodayAppointments;

  const upcomingAppointments = appointmentsForNext
    .filter(appt => {
      if (!appt.time) return false;
      const [hours, minutes] = appt.time.split(':').map(Number);
      const apptTimeInMinutes = hours * 60 + minutes;
      return apptTimeInMinutes > currentTimeInMinutes;
    })
    .sort((a, b) => {
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });

  const nextAppointment = upcomingAppointments[0];
  const nextAppointmentTime = nextAppointment ? nextAppointment.time : 'No more today';
  const nextAppointmentClient = nextAppointment ? nextAppointment.clientName || 'Unknown Client' : 'All done!';
  const remainingAppointments = upcomingAppointments.length;

  // Staff on site - based on all appointments for today (not just upcoming)
  const getStaffOnSite = () => {
    const staffWithAppointments = new Set();
    // Use all today's appointments, not just upcoming ones
    const allTodayAppointments = appointments.filter(appt => appt.date === today);
    allTodayAppointments.forEach(appt => {
      if (appt.staffId) {
        staffWithAppointments.add(appt.staffId);
      }
    });
    return staffWithAppointments.size;
  };

  const staffOnSite = getStaffOnSite();

  // Outstanding invoices - count actual unpaid invoices from invoice service
  const outstandingInvoices = invoices.filter(invoice =>
    invoice.status === 'unpaid'
  ).length;

  // Get role-based 4th stats box
  const getRoleBasedStats = () => {
    const role = effectiveRole?.toLowerCase() || 'staff';

    switch (role) {
      case 'admin':
        return {
          title: "Staff on Site",
          value: staffOnSite,
          change: "With remaining appointments",
          changeType: "positive",
          icon: FiUserCheck,
          color: "purple"
        };
      case 'clinician':
        return {
          title: "Next Appointment",
          value: nextAppointmentTime,
          change: remainingAppointments > 0 ? `${remainingAppointments} left today` : nextAppointmentClient,
          changeType: "neutral",
          icon: FiClock,
          color: "purple"
        };
      case 'staff':
      default:
        return {
          title: "Outstanding Invoices",
          value: outstandingInvoices,
          change: "Awaiting payment",
          changeType: "negative",
          icon: FiDollarSign,
          color: "purple"
        };
    }
  };

  // Filter for logged-in clinician's incomplete notes
  const toDoNotes = appointments.filter((appt) => {
    if (user.role === "admin" || user.role === "manager") return ["red", "amber"].includes(appt.noteStatus);
    if (user.role === "clinician") {
      return (
        appt.staffId === user.userId &&
        ["red", "amber"].includes(appt.noteStatus)
      );
    }
    return false;
  });

  // Real-time activity listener
  useEffect(() => {
    const handleActivityLogged = (event) => {
      const newActivity = event.detail;
      setRecentActivity(prev => [newActivity, ...prev.slice(0, 5)]); // Keep only 6 most recent
    };

    window.addEventListener('activityLogged', handleActivityLogged);
    return () => window.removeEventListener('activityLogged', handleActivityLogged);
  }, []);

  // Helper function to format activity time
  const formatActivityTime = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Helper function to get icon component from string
  const getIconComponent = (iconName) => {
    const iconMap = {
      FiCalendar,
      FiCheckCircle,
      FiFileText,
      FiGrid,
      FiUser,
      FiDollarSign,
      FiClock,
      FiActivity,
      FiAlertTriangle
    };
    return iconMap[iconName] || FiActivity;
  };

  // Role-specific stats configuration
  const getStatsConfig = () => {
    const baseStats = [
      {
        title: role === 'clinician' ? "My Appointments Today" : "Today's Appointments",
        value: todayAppointments.length,
        change: "+12%",
        changeType: "positive",
        icon: FiCalendar,
        color: "blue"
      },
      {
        title: role === 'clinician' ? "My Clients Today" : "Total Clients",
        value: filteredClients.length,
        change: "+8%",
        changeType: "positive",
        icon: FiUsers,
        color: "green"
      },
      {
        title: "Pending Notes",
        value: pendingNotes.length,
        change: "-5%",
        changeType: "negative",
        icon: FiFileText,
        color: "orange"
      },
      getRoleBasedStats()
    ];

    return baseStats;
  };

  // Admin gets additional clinic-wide stats
  const getAdminClinicStats = () => {
    if (role !== 'admin') return [];

    return [
      {
        title: "Total Clinic Appointments",
        value: appointments.filter(appt => appt.date === today).length,
        change: "Clinic-wide today",
        changeType: "neutral",
        icon: FiCalendar,
        color: "indigo"
      },
      {
        title: "Total Clinic Clients",
        value: clients.length,
        change: "All registered",
        changeType: "neutral",
        icon: FiUsers,
        color: "teal"
      },
      {
        title: "Outstanding Invoices",
        value: outstandingInvoices,
        change: "Awaiting payment",
        changeType: "negative",
        icon: FiDollarSign,
        color: "red"
      },
      {
        title: "Staff on Site",
        value: staffOnSite,
        change: "Currently working",
        changeType: "positive",
        icon: FiUserCheck,
        color: "emerald"
      }
    ];
  };

  const stats = getStatsConfig();
  const adminStats = getAdminClinicStats();

  const quickActions = [
    {
      title: "New Appointment",
      description: "Schedule a new client appointment",
      icon: FiPlus,
      action: () => navigate('/calendar'),
      color: "blue"
    },
    {
      title: "Add Client",
      description: "Register a new client",
      icon: FiUsers,
      action: () => navigate('/clients'),
      color: "green"
    },
    {
      title: "View Waitlist",
      description: "Manage client waitlist",
      icon: FiClock,
      action: () => navigate('/waitlist'),
      color: "orange"
    },
    {
      title: "Treatment Room Information",
      description: "View room details and availability",
      icon: FiGrid,
      action: () => navigate('/rooms'),
      color: "purple"
    }
  ];

  const getColorClasses = (color, type = 'bg') => {
    const colors = {
      blue: type === 'bg' ? 'bg-blue-500' : type === 'text' ? 'text-blue-600' : 'border-blue-200',
      green: type === 'bg' ? 'bg-green-500' : type === 'text' ? 'text-green-600' : 'border-green-200',
      orange: type === 'bg' ? 'bg-orange-500' : type === 'text' ? 'text-orange-600' : 'border-orange-200',
      purple: type === 'bg' ? 'bg-purple-500' : type === 'text' ? 'text-purple-600' : 'border-purple-200',
      indigo: type === 'bg' ? 'bg-indigo-500' : type === 'text' ? 'text-indigo-600' : 'border-indigo-200',
      teal: type === 'bg' ? 'bg-teal-500' : type === 'text' ? 'text-teal-600' : 'border-teal-200',
      red: type === 'bg' ? 'bg-red-500' : type === 'text' ? 'text-red-600' : 'border-red-200',
      emerald: type === 'bg' ? 'bg-emerald-500' : type === 'text' ? 'text-emerald-600' : 'border-emerald-200'
    };
    return colors[color] || colors.blue;
  };

  // Show loading state while tenant context is initializing
  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tenant context...</p>
        </div>
      </div>
    );
  }

  // Show message if no tenant is available
  if (!currentTenant) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FiAlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Tenant Access</h2>
          <p className="text-gray-600">Please contact your administrator to set up clinic access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Personal/Role-specific Statistics Cards */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {role === 'admin' ? 'My Personal Stats' :
              role === 'clinician' ? 'My Dashboard' :
                'Your Overview'}
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="card-modern p-4 md:p-6 min-h-[120px] md:min-h-[140px]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-2 md:p-3 rounded-lg ${getColorClasses(stat.color)} bg-opacity-10`}>
                    <Icon className={`h-5 w-5 md:h-6 md:w-6 ${getColorClasses(stat.color, 'text')}`} />
                  </div>
                </div>
                <div className="mt-2 md:mt-4 flex items-center">
                  <span className={`text-xs md:text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                    stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                    {stat.change}
                  </span>
                  {stat.changeType !== 'neutral' && (
                    <span className="text-xs md:text-sm text-gray-500 ml-1 md:ml-2 hidden sm:inline">from last month</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Admin Clinic-wide Statistics */}
      {role === 'admin' && adminStats.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900">Clinic Overview</h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">Admin View</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {adminStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={`admin-${index}`} className="card-modern p-4 md:p-6 border-l-4 border-l-blue-500 min-h-[120px] md:min-h-[140px]">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">{stat.value}</p>
                    </div>
                    <div className={`p-2 md:p-3 rounded-lg ${getColorClasses(stat.color)} bg-opacity-10`}>
                      <Icon className={`h-5 w-5 md:h-6 md:w-6 ${getColorClasses(stat.color, 'text')}`} />
                    </div>
                  </div>
                  <div className="mt-2 md:mt-4 flex items-center">
                    <span className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                      stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                      {stat.change}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Left Column - Pending Tasks */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-modern p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <FiAlertCircle className="mr-2 text-orange-500" />
                Pending Tasks
              </h2>
              <span className="badge badge-warning">{toDoNotes.length} pending</span>
            </div>

            {toDoNotes.length === 0 ? (
              <div className="text-center py-8">
                <FiCheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
                <p className="text-gray-500">No pending clinical notes. Great work!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {toDoNotes.slice(0, 5).map((appt) => {
                  const client = clients.find((c) => c.id === appt.clientId);
                  const staff = staffList.find((s) => s.id === appt.staffId);
                  return (
                    <div
                      key={appt.id}
                      className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${appt.noteStatus === 'red' ? 'bg-red-500' : 'bg-yellow-500'
                            }`} />
                          <div>
                            <p className="font-medium text-gray-900">
                              {client ? client.name : "Unknown Client"}
                            </p>
                            <p className="text-sm text-gray-600">
                              {appt.date} at {appt.time} • {staff ? staff.name : "Unknown Staff"}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 ml-6">
                          Status: {appt.noteStatus === "red" ? "Not started" : "Draft saved"}
                        </p>
                      </div>
                      <button
                        className="btn-primary text-sm px-4 py-3 min-h-[44px] flex items-center justify-center"
                        onClick={() =>
                          navigate(`/clients/${appt.clientId}?tab=notes&appt=${appt.id}`)
                        }
                      >
                        <span className="mr-2">Complete Note</span>
                        <FiArrowRight size={16} />
                      </button>
                    </div>
                  );
                })}
                {toDoNotes.length > 5 && (
                  <div className="text-center pt-4">
                    <button className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                      View all {toDoNotes.length} pending tasks
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Admin Management Tiles - Side by Side */}
          {userRole === 'admin' && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {/* Booking Requests - Compact */}
              <BookingRequestsTile />

              {/* Staff Management - Compact */}
              <div className="card-modern p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-bold text-gray-900 flex items-center">
                    <FiUsers className="w-4 h-4 mr-2 text-blue-600" />
                    Staff Management
                  </h3>
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                    {pendingLeaveRequests.length} pending
                  </span>
                </div>
                {pendingLeaveRequests.length === 0 ? (
                  <div className="text-center py-4">
                    <FiCheckCircle className="mx-auto h-6 w-6 text-green-500 mb-2" />
                    <p className="text-xs text-gray-500">All requests reviewed</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pendingLeaveRequests.slice(0, 2).map((request) => (
                      <div key={request.id} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{request.staffName}</p>
                          <p className="text-xs text-gray-600">{request.type} • {new Date(request.startDate).toLocaleDateString()}</p>
                        </div>
                        <button
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          onClick={() => navigate(`/admin?tab=staff&staffId=${request.staffId}`)}
                        >
                          Review
                        </button>
                      </div>
                    ))}
                    {pendingLeaveRequests.length > 2 && (
                      <div className="text-center pt-2">
                        <button 
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={() => navigate('/admin?tab=staff')}
                        >
                          View {pendingLeaveRequests.length - 2} more
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Quick Actions */}
        <div className="space-y-4">
          {/* Quick Actions - Compact */}
          <div className="card-modern p-4">
            <h3 className="text-base font-bold text-gray-900 mb-3">Quick Actions</h3>
            <div className="grid grid-cols-1 gap-2">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className="w-full flex items-center p-2 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all group"
                  >
                    <div className={`p-2 rounded ${getColorClasses(action.color)} bg-opacity-10 group-hover:bg-opacity-20 transition-colors`}>
                      <Icon className={`h-4 w-4 ${getColorClasses(action.color, 'text')}`} />
                    </div>
                    <div className="ml-3 text-left flex-1">
                      <p className="text-sm font-medium text-gray-900">{action.title}</p>
                    </div>
                    <FiArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </div>


    </div>
  );
}
